import moment from 'moment-timezone'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import { buildCsvFilename, downloadCsv } from '@/utils/file'

import { csvReservationsFields } from '../../../helpers/constants'
import {
  getFullDateFromDayMinutes,
  getTimezoneStamp,
  getDayNameByNumber,
} from '../../../helpers/helpers'
import {
  cancelAmenityReservations,
  deleteAmenity,
  getAmenities,
  getMultiDayAmenityReservationsInDateRange,
  selectAmenities,
  selectAmenityReservations,
  selectMultiDayAmenityReservations,
  clearReservations,
} from '../../../reducers/Organisation/reservationsReducer'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import { selectUserData, selectUserRole } from '../../../reducers/userDataReducer'
import FeaturedTable from '../../FeaturedTable/FeaturedTable'
import DateField from '../../FormLegacy/FormFieldsV2/DateField'
import SelectField from '../../FormLegacy/FormFieldsV2/SelectField'
import Icon from '../../Icons'

import SingleReservationDetails from './SingleReservationDetails'

const emptySymbol = '—'

const getColumns = (t) => [
  {
    id: 'date',
    Header: t('reservations:detailedView.columns.date'),
    accessor: 'date',
    Cell: ({ row, value }) => {
      if (row.original.isMultiDay) return `${value} - ${row.original.endDate}`
      return value
    },
  },
  {
    Header: t('reservations:detailedView.columns.timeType'),
    accessor: 'clientId',
    id: 'apartment',
    Cell: ({ row }) => {
      if (!row.original.isMultiDay && row.original.startTime && row.original.endTime) {
        return `${row.original.startTime} - ${row.original.endTime}`
      }
      if (row.original.isPhantomBooking) return emptySymbol
      if (row.original.isUnavailableSlot) return emptySymbol
      return emptySymbol
    },
  },
  {
    Header: t('reservations:detailedView.columns.amenity'),
    accessor: 'amenity',
    Cell: ({ value }) => value,
  },
  {
    Header: t('reservations:detailedView.columns.price'),
    accessor: 'price',
    Cell: ({ value }) => value,
  },
  {
    Header: t('reservations:detailedView.columns.name'),
    accessor: 'firstName',
    Cell: (obj) => `${obj.row.original.firstName} ${obj.row.original.lastName}`.trim(),
  },
  {
    Header: t('reservations:detailedView.columns.apartmentNumber'),
    accessor: 'unit',
    Cell: ({ value }) => value,
  },
  {
    id: 'arrow',
    accessor: 'onClickHandler',
    Header: () => null,

    Cell: ({ value }) => (
      <div className={`ctp-maintenance-requests-list__arrow`} onClick={value}>
        <Icon icon={'arrow'} />
      </div>
    ),
  },
]

const DetailedView = ({
  getAmenities,
  building,
  selectedReservations,
  getMultiDayAmenityReservationsInDateRange,
  getAmenityReservationsInDateRange,
  amenities,
  selectedMultidayReservations,
  userRole,
  clearReservations,
}) => {
  const { t } = useTranslation(['reservations'])
  const columns = getColumns(t)
  const buildingId = building ? building.id : null
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedAmenity, setSelectedAmenity] = useState('all')
  const navigate = useNavigate()
  const params = useParams()

  const fetchReservations = async () => {
    try {
      if (params.from && params.to) {
        getAmenities(buildingId)
        await getAmenityReservationsInDateRange(buildingId, Number(params.from), Number(params.to))
        await getMultiDayAmenityReservationsInDateRange(
          buildingId,
          Number(params.from),
          Number(params.to),
        )
      }
    } catch (err) {
      console.error('Error while fetching Reservations: ' + err)
    }
  }

  const getFormatedDate = (date) => {
    return moment(Number(date))
      .tz(building.timezone || 'America/Chicago')
      .format('MM/DD/YY')
  }

  const getFormatedTime = (time) => {
    return moment(getFullDateFromDayMinutes(time)).format('h:mma')
  }

  const getReservationClickHandler = (itemId) => {
    return () =>
      navigate(
        `/dashboard/building-dashboard/reservations/detailed-view/${params.from}/${params.to}/${itemId}`,
      )
  }

  const isInTimeRange = (timeFrom, timeTo) => {
    return (
      (params.from >= timeFrom && params.from <= timeTo) ||
      (params.to >= timeFrom && params.to <= timeTo) ||
      (params.from <= timeFrom && params.to >= timeTo)
    )
  }

  const isGivenWeekdayIsInTimeRange = (weekdayNum) => {
    const milisecondsInOneDay = 86400000
    const dayOfWeekFrom = new Date(+params.from).getDay()
    const daysRange = Math.ceil((+params.to - +params.from) / milisecondsInOneDay)
    if (daysRange > 6) return true
    const weekDaysRangeArr = [dayOfWeekFrom]
    new Array(daysRange).fill().forEach((_, index) => {
      let nextDayOfWeek = dayOfWeekFrom + (index + 1)
      if (nextDayOfWeek > 6) {
        nextDayOfWeek = nextDayOfWeek - 7
      }
      weekDaysRangeArr.push(nextDayOfWeek)
    })
    return weekDaysRangeArr.includes(weekdayNum)
  }

  const filterForIsInTimeRange = (unavailableSlotsOrPhantomBookings) => {
    if (!params.from || !params.to) return []
    return unavailableSlotsOrPhantomBookings
      .filter((item) => {
        const isPhantomBookingItem = item.isPhantomBooking && !item.isUnavailableSlot
        const unavailableSlotItem = !item.isPhantomBooking && item.isUnavailableSlot
        if (isPhantomBookingItem) {
          return isInTimeRange(item.originalTime.startDate, item.originalTime.endDate)
        } else if (unavailableSlotItem) {
          const isWeekly = String(item.originalTime.date).length === 1
          if (isWeekly) {
            return isGivenWeekdayIsInTimeRange(item.originalTime.date)
          } else {
            return isInTimeRange(item.originalTime.date, item.originalTime.date)
          }
        }
      })
      .filter((slot) => {
        if (selectedAmenity === 'all') return true
        const foundAmenity = amenities.find((amenity) => amenity.id === selectedAmenity)
        return foundAmenity ? foundAmenity.name === slot.amenity.name : false
      })
  }

  const getAmenityInfoForSlotOrPhantom = (unavailableSlotOrPhantomBooking, amenity) => {
    const { date, startDate, endDate, startTime, endTime } = unavailableSlotOrPhantomBooking
    const isDateDefined = date !== undefined
    const isDateIsDayOfWeek = isDateDefined && String(date).length === 1
    return {
      id: amenity.id,
      amenity: amenity.name,
      date: isDateDefined
        ? isDateIsDayOfWeek
          ? getDayNameByNumber(date)
          : getFormatedDate(date)
        : getFormatedDate(startDate),
      startDate: getFormatedDate(startDate),
      endDate: isDateDefined ? '' : getFormatedDate(endDate),
      startTime: startTime !== undefined ? getFormatedTime(startTime) : '',
      endTime: endTime !== undefined ? getFormatedTime(endTime) : '',
      lastName: '',
      phone: emptySymbol,
      unit: emptySymbol,
      price: emptySymbol,
      refund: 'false',
      cancelled: true,
      onClickHandler: getReservationClickHandler(amenity.id),
      originalTime: unavailableSlotOrPhantomBooking,
    }
  }

  const { managerialBooking, unavailableSlots } = useMemo(
    () =>
      amenities
        .map((amenity) => ({
          managerialBooking: (amenity.managerialBooking || []).map((item) => ({
            ...item,
            ...getAmenityInfoForSlotOrPhantom(item, amenity),
          })),
          unavailableSlots: amenity.unavailableSlots
            ? {
                regular: amenity.unavailableSlots.regular.map((regularSlot) => ({
                  ...regularSlot,
                  ...getAmenityInfoForSlotOrPhantom(regularSlot, amenity),
                })),
                weekly: amenity.unavailableSlots.weekly.map((weeklySlot) => ({
                  ...weeklySlot,
                  ...getAmenityInfoForSlotOrPhantom(weeklySlot, amenity),
                })),
              }
            : null,
        }))
        .reduce(
          (prev, next) => ({
            managerialBooking: [...prev.managerialBooking, next.managerialBooking]
              .flat(1)
              .map((phantom) => ({
                ...phantom,
                firstName: t('reservations:detailedView.phantomBookingLabel'),
                isPhantomBooking: true,
                isUnavailableSlot: false,
                isMultiDay: true,
              })),

            // [next.unavailableSlots.regular, next.unavailableSlots.weekly] temporary fix
            unavailableSlots: [
              ...prev.unavailableSlots,
              ...(next.unavailableSlots ? [next.unavailableSlots.regular] : []),
            ]
              // unavailableSlots: [
              //   ...prev.unavailableSlots,
              //   ...(next.unavailableSlots
              //     ? [next.unavailableSlots.regular, next.unavailableSlots.weekly]
              //     : [])
              // ]
              .flat(1)
              .map((slot) => ({
                ...slot,
                firstName: t('reservations:detailedView.unavailableSlotLabel'),
                isPhantomBooking: false,
                isUnavailableSlot: true,
                isMultiDay: false,
              })),
          }),
          { managerialBooking: [], unavailableSlots: [] },
        ),
    [amenities],
  )

  const [selectedStartDate, setSelectedStartDate] = useState(
    moment(Number(params.from || new Date())),
  )

  const [selectedEndDate, setSelectedEndDate] = useState(moment(Number(params.to || new Date())))

  useEffect(() => {
    navigate(
      `/dashboard/building-dashboard/reservations/detailed-view/${getTimezoneStamp(
        selectedStartDate.toDate().getTime(),
        [building],
      )}/${
        getTimezoneStamp(selectedEndDate.toDate().getTime(), [building]) || ''
      }/${params.reservationId || ''}`,
    )
    return () => {
      clearReservations()
    }
  }, [buildingId])

  const data = useMemo(
    () => [
      ...selectedReservations
        .map((amenity) => {
          const reservations = !amenity.reservations
            ? []
            : amenity.reservations
                .filter((reservation) => {
                  if (selectedStatus === 'all') return true
                  if (
                    reservation.cancelled &&
                    reservation.approved &&
                    selectedStatus === 'cancelled'
                  ) {
                    return true
                  }

                  return (
                    reservation.approved && !reservation.cancelled && selectedStatus === 'approved'
                  )
                })
                .filter(() => {
                  if (selectedAmenity === 'all') return true
                  const foundAmenity = amenities.find((amenity) => amenity.id === selectedAmenity)
                  return foundAmenity ? foundAmenity.name === amenity.amenity.name : false
                })
          return reservations.map((reservation) => {
            return {
              date: getFormatedDate(reservation.date),
              startTime: getFormatedTime(amenity.startTime),
              endTime: getFormatedTime(amenity.startTime + reservation.duration),
              amenity: amenity.amenity.name,
              duration: reservation.duration + 'min',
              price: reservation.chargeAmount || emptySymbol,
              refund: reservation.refund || 'false',
              firstName: reservation.client
                ? reservation.client.firstName
                : reservation.guest.firstName,
              lastName: reservation.client
                ? reservation.client.lastName
                : reservation.guest.lastName,
              unit: reservation.client ? reservation.client.unit : emptySymbol,
              phone: reservation.client ? reservation.client.phone : reservation.guest.phone,
              onClickHandler: getReservationClickHandler(reservation.id),
            }
          })
        })
        .flat(3),
      ...selectedMultidayReservations
        .filter((reservation) => {
          if (selectedStatus === 'all') return true
          if (reservation.cancelled && reservation.approved && selectedStatus === 'cancelled') {
            return true
          }

          return reservation.approved && !reservation.cancelled && selectedStatus === 'approved'
        })
        .filter((reservation) => {
          if (selectedAmenity === 'all') return true
          const foundAmenity = amenities.find((amenity) => amenity.id === selectedAmenity)
          return foundAmenity ? foundAmenity.name === reservation.amenity.name : false
        })
        .map((amenity) => {
          return {
            isMultiDay: true,
            date: getFormatedDate(amenity.date),
            amenity: amenity.amenity.name,
            endDate: getFormatedDate(amenity.endDate),
            price: amenity.chargeAmount || emptySymbol,
            refund: amenity.refund || 'false',
            firstName: amenity.client ? amenity.client.firstName : amenity.guest.firstName,
            lastName: amenity.client ? amenity.client.lastName : amenity.guest.lastName,
            unit: amenity.client ? amenity.client.unit : emptySymbol,
            phone: amenity.client ? amenity.client.phone : amenity.guest.phone,
            isPhantomBooking: managerialBooking.some((singleBooking) => {
              return (
                singleBooking &&
                singleBooking.startDate <= amenity.date &&
                singleBooking.endDate >= amenity.endDate
              )
            }),
            isBlockEntireDay: unavailableSlots.some(
              (unavailableSlot) => unavailableSlot.date === amenity.date,
            ),
            onClickHandler: getReservationClickHandler(amenity.id),
          }
        }),
      ...filterForIsInTimeRange(managerialBooking),
      ...filterForIsInTimeRange(unavailableSlots),
    ],
    [
      selectedReservations,
      selectedMultidayReservations,
      selectedStatus,
      selectedAmenity,
      amenities,
    ],
  )

  const getPhantomOrUnavailable = (amenityId) => {
    return [...managerialBooking, ...unavailableSlots].find((item) => item.id === Number(amenityId))
  }

  const selectedReservation = useMemo(() => {
    if (!params.reservationId) return null

    const phantomOrUnavailable = getPhantomOrUnavailable(params.reservationId)
    if (phantomOrUnavailable) {
      return {
        isMultiDay: phantomOrUnavailable.isMultiDay,
        reservation: {
          ...phantomOrUnavailable,
          client: phantomOrUnavailable,
          reservations: [{ ...phantomOrUnavailable, client: phantomOrUnavailable }],
        },
      }
    }

    const foundReservation = selectedReservations.map((amenity) => {
      return {
        ...amenity,
        reservations: amenity.reservations.filter(
          (reservation) => Number(reservation.id) === Number(params.reservationId),
        ),
      }
    })

    const foundSingleDayReservation = foundReservation.find(
      (amenity) => amenity.reservations.length,
    )
    if (foundSingleDayReservation) {
      return { isMultiDay: false, reservation: foundSingleDayReservation }
    }

    return {
      isMultiDay: true,
      reservation: selectedMultidayReservations.find(
        (amenity) => amenity.id === Number(params.reservationId),
      ),
    }
  }, [
    selectedReservations,
    selectedMultidayReservations,
    selectedStatus,
    selectedAmenity,
    params.reservationId,
    managerialBooking,
    unavailableSlots,
  ])

  const pickStartDay = (startDate) => {
    const from = Number(params.from)
    let startTime = from !== from ? new Date().getTime() : from

    if (startDate) {
      setSelectedStartDate(startDate)
      startTime = startDate
    }

    const startTargetUnixTime = new Date(startTime).getTime()

    navigate(
      `/dashboard/building-dashboard/reservations/detailed-view/${getTimezoneStamp(
        startTargetUnixTime,
        [building],
      )}/${
        getTimezoneStamp(selectedEndDate.toDate().getTime(), [building]) || ''
      }/${params.reservationId || ''}`,
    )
  }

  const pickEndDay = (endDate) => {
    const to = Number(params.to)

    let endTime = to !== to ? new Date().getTime() : to

    if (endDate) {
      setSelectedEndDate(endDate)
      endTime = endDate
    }

    const endTargetUnixTime = new Date(endTime).getTime()

    navigate(
      `/dashboard/building-dashboard/reservations/detailed-view/${
        getTimezoneStamp(selectedStartDate.toDate().getTime(), [building]) || ''
      }/${getTimezoneStamp(endTargetUnixTime, [building])}/${params.reservationId || ''}`,
    )
  }

  const downloadCSVList = (e) => {
    e.preventDefault()
    if (selectedReservations.length === 0) return
    const data = []

    selectedReservations.map((amenity) => {
      const reservations = !amenity.reservations
        ? []
        : amenity.reservations
            .filter((reservation) => {
              if (selectedStatus === 'all') return true
              if (reservation.cancelled && reservation.approved && selectedStatus === 'cancelled') {
                return true
              }

              return reservation.approved && selectedStatus === 'approved'
            })
            .filter(() => {
              if (selectedAmenity === 'all') return true
              const foundAmenity = amenities.find((amenity) => amenity.id === selectedAmenity)
              return foundAmenity ? foundAmenity.name === amenity.amenity.name : false
            })
      return reservations.map((reservation) =>
        data.push({
          date: getFormatedDate(reservation.date),
          time: getFormatedTime(amenity.startTime),
          amenity: amenity.amenity.name,
          duration: reservation.duration + 'min',
          price: reservation.chargeAmount || emptySymbol,
          refund: reservation.refund || 'false',
          firstName: reservation.client
            ? reservation.client.firstName
            : reservation.guest.firstName,
          lastName: reservation.client ? reservation.client.lastName : reservation.guest.lastName,
          unit: reservation.client ? reservation.client.unit : emptySymbol,
          phone: reservation.client ? reservation.client.phone : reservation.guest.phone,
          email: reservation.client ? reservation.client.email : reservation.guest.email,
        }),
      )
    })

    downloadCsv({
      data,
      fields: csvReservationsFields,
      filename: buildCsvFilename({
        building: building?.name || null,
        page: 'reservations',
        dateRange: [Number(params.from), Number(params.to)],
        timezone: building?.timezone,
        context: params.reservationId ? [`ID-${params.reservationId}`] : [],
      }),
    })
  }

  return (
    <div className="ctp-maintenance-requests-list ctp-guests-residents-list detailed-view-container">
      <div className="reservation-filters-container">
        <div className="reservation-date-pickers-container">
          <DateField
            label={t('reservations:detailedView.filters.startDate')}
            selected={selectedStartDate.toDate()}
            name="reservations"
            onChange={(e) => pickStartDay(moment(e.target.value))}
            startDate={selectedStartDate.toDate()}
            endDate={selectedEndDate.toDate()}
            additionalClass="reservation-picker"
          />
          <DateField
            label={t('reservations:detailedView.filters.endDate')}
            selected={selectedEndDate.toDate()}
            name="reservations"
            onChange={(e) => {
              pickEndDay(moment(e.target.value))
            }}
            startDate={selectedStartDate.toDate()}
            endDate={selectedEndDate.toDate()}
            minDate={selectedStartDate.toDate()}
            additionalClass="reservation-picker"
          />
        </div>
        <div className="reservation-right-filters-container">
          <SelectField
            name="Amenity"
            options={[
              {
                value: 'all',
                label: t('reservations:detailedView.filters.allOption'),
              },
              ...amenities
                .map((amenity) => ({ value: amenity.id, label: amenity.name }))
                .sort((a, b) => {
                  const textA = a.label.toUpperCase()
                  const textB = b.label.toUpperCase()
                  return textA < textB ? -1 : textA > textB ? 1 : 0
                }),
            ]}
            label={t('reservations:detailedView.columns.amenity')}
            placeholder={t('reservations:detailedView.columns.amenity')}
            value={selectedAmenity}
            isError={false}
            style={{ width: '40%' }}
            onChange={(e) => setSelectedAmenity(e.target.value)}
          />
          <SelectField
            name="Status"
            options={[
              {
                value: 'all',
                label: t('reservations:detailedView.filters.allOption'),
              },
              {
                value: 'approved',
                label: t('reservations:detailedView.filters.activeOption'),
              },
              {
                value: 'cancelled',
                label: t('reservations:detailedView.filters.cancelledOption'),
              },
            ]}
            label={t('reservations:detailedView.filters.statusLabel')}
            placeholder={t('reservations:detailedView.filters.statusLabel')}
            value={selectedStatus}
            isError={false}
            style={{ width: '40%' }}
            onChange={(e) => setSelectedStatus(e.target.value)}
          />
          <button
            className="csv-button-reservation button button-primary has-icon ctp-toolbar__csv"
            onClick={downloadCSVList}
          >
            <Icon icon={'download'} />
            {t('reservations:detailedView.filters.csv')}
          </button>
          <button
            className="csv-button-reservation button button-primary has-icon ctp-toolbar__csv"
            onClick={() => fetchReservations()}
          >
            <Icon icon={'search'} />
            {t('reservations:detailedView.filters.search')}
          </button>
        </div>
      </div>

      <div className="reservation-list-container">
        <div className="request-details-list-container">
          {data.length ? (
            <FeaturedTable
              columns={columns}
              data={data}
              initialState={{
                sortBy: [
                  {
                    id: 'date',
                    asc: true,
                  },
                ],
              }}
            />
          ) : null}
        </div>
        {selectedReservation && (
          <SingleReservationDetails
            reservation={selectedReservation.reservation}
            isMultiDay={selectedReservation.isMultiDay}
            refetch={() => fetchReservations()}
            userRole={userRole}
          />
        )}
      </div>
    </div>
  )
}

export default connect(
  (state) => {
    return {
      amenities: selectAmenities(state),
      userData: selectUserData(state),
      building: selectActiveBuilding(state),
      userRole: selectUserRole(state),
      selectedReservations: selectAmenityReservations(state),
      selectedMultidayReservations: selectMultiDayAmenityReservations(state),
    }
  },
  {
    getAmenities,
    getMultiDayAmenityReservationsInDateRange,
    cancelAmenityReservations,
    deleteAmenity,
    clearReservations,
  },
)(DetailedView)
